/**
 * Shared helpers for arrangement directives.
 *
 * directiveIdentity mirrors the Python engine's canonical directive identity —
 * (type, normalised scope) — computed by directive_identity() in
 * planigo-ai-simulation app/utils/directives.py, the same key merge_directives
 * uses for cross-layer override. The free-text instruction and the LLM
 * interpretation are never part of identity: two directives that target the
 * same thing are the same directive, however they were phrased.
 */

// Stable string key for a directive. Scope keys are sorted, so equal scopes
// written in different orders produce the same key.
export const directiveIdentity = (directive) => {
    const scope = (directive && directive.scope) || {};
    const scopeEntries = Object.keys(scope)
        .sort()
        .map((key) => [key, scope[key]]);
    return JSON.stringify([(directive && directive.type) || null, scopeEntries]);
};

// Returns the entry in `entries` (each shaped {directive, ...}) whose
// directive has the same identity as `directive`, or undefined.
export const findDuplicateDirective = (entries, directive) => {
    const identity = directiveIdentity(directive);
    return (entries || []).find((e) => directiveIdentity(e.directive) === identity);
};

// Directive value → translation key. Resolved with t() at render time.
export const ENTITY_TYPE_KEY = {
    brand: 'DIR_ENTITY_BRAND',
    supplier: 'DIR_ENTITY_SUPPLIER',
    subcategory: 'DIR_ENTITY_SUBCATEGORY',
    category: 'DIR_ENTITY_CATEGORY',
};
const ZONE_KEY = { top: 'DIR_ZONE_TOP', eye: 'DIR_ZONE_EYE', bottom: 'DIR_ZONE_BOTTOM' };
const ANCHOR_KEY = { left: 'DIR_ANCHOR_LEFT', right: 'DIR_ANCHOR_RIGHT' };
const POS_KEY = { start: 'DIR_POS_START', end: 'DIR_POS_END' };
const SORTKEY_KEY = {
    profit: 'DIR_SORT_PROFIT',
    sales: 'DIR_SORT_SALES',
    price: 'DIR_SORT_PRICE',
    salesUnits: 'DIR_SORT_UNITS',
    name: 'DIR_SORT_NAME',
};

// The arrow in a rule description points the way the language reads. U+2192 is
// not bidi-mirrored, so in Hebrew it would otherwise keep pointing right (F5).
const arrow = (rtl) => (rtl ? '←' : '→');

const SCOPE_ID_KEYS = {
    brandId: ['brands', 'DIR_ENTITY_BRAND'],
    supplierId: ['suppliers', 'DIR_ENTITY_SUPPLIER'],
    subCategoryId: ['subcategories', 'DIR_ENTITY_SUBCATEGORY'],
    categoryId: ['categories', 'DIR_ENTITY_CATEGORY'],
};

/**
 * Human-readable, translated description of a directive — the text the Rules
 * tab shows for each rule. Scope IDs resolve to names via the edition catalog
 * when it is loaded. `rtl` flips the directional arrow, so the description
 * reads correctly in Hebrew as well as English.
 */
export const describeDirective = (directive, catalog, t, rtl = false) => {
    if (!directive) return '';
    const to = arrow(rtl);

    const nameOf = (scope) => {
        if (!scope) return '';
        for (const key of Object.keys(SCOPE_ID_KEYS)) {
            if (scope[key] != null) {
                const [listKey, labelKey] = SCOPE_ID_KEYS[key];
                const found = ((catalog && catalog[listKey]) || []).find((e) => e.id === scope[key]);
                return `${t(labelKey)} ${found ? found.name : scope[key]}`;
            }
        }
        return '';
    };

    switch (directive.type) {
        case 'zone':
            return `${nameOf(directive.scope)} ${to} ${ZONE_KEY[directive.zone] ? t(ZONE_KEY[directive.zone]) : directive.zone}`;
        case 'pin':
            return `${nameOf(directive.scope)} ${to} ${ANCHOR_KEY[directive.anchor] ? t(ANCHOR_KEY[directive.anchor]) : directive.anchor}`;
        case 'pullOut':
            return `${nameOf(directive.scope)} ${to} ${t('DIR_DESC_SEPARATE_BLOCK')} ${directive.position && POS_KEY[directive.position] ? t(POS_KEY[directive.position]) : ''}`;
        case 'orderBy':
            return `${nameOf(directive.scope)} ${to} ${t('DIR_DESC_SORT_BY')} ${SORTKEY_KEY[directive.key] ? t(SORTKEY_KEY[directive.key]) : directive.key}`;
        case 'hierarchyOrder':
            return `${t('DIR_DESC_GROUP_ORDER')}: ${(directive.levels || []).map((l) => (ENTITY_TYPE_KEY[l] ? t(ENTITY_TYPE_KEY[l]) : l)).join(` ${to} `)}`;
        default:
            return directive.type;
    }
};

// Deterministic compiler failure code → translation key. The compiler decides
// these itself (they never come from the model), so they are rendered in the
// app language. Mirrors the FAILURE CODES in planigo-ai-simulation
// app/services/directive_compiler.py — the two lists must stay in step.
export const COMPILE_ERROR_KEY = {
    INSTRUCTION_REQUIRED: 'DIR_ERR_INSTRUCTION_REQUIRED',
    UNSUPPORTED_SCOPE_TYPE: 'DIR_ERR_UNSUPPORTED_SCOPE_TYPE',
    AMBIGUOUS_ENTITY_NAME: 'DIR_ERR_AMBIGUOUS_ENTITY_NAME',
    ENTITY_NOT_FOUND: 'DIR_ERR_ENTITY_NOT_FOUND',
    COMPILE_FAILED: 'DIR_ERR_COMPILE_FAILED',
    DIRECTIVES_DISABLED: 'DIR_ERR_DIRECTIVES_DISABLED',
};

/**
 * The text to show for a non-ok compile response.
 *
 * A known `code` is a deterministic compiler failure: translate it, so it reads
 * in the app language. Anything else — a clarification the model authored, or a
 * code this client does not know — falls back to the raw message, which for a
 * model-authored one is already in the language of the user's instruction.
 */
export const compileFailureMessage = (response, t) => {
    const key = response && COMPILE_ERROR_KEY[response.code];
    if (!key) return (response && response.message) || '';

    const params = (response && response.params) || {};
    const entityType = ENTITY_TYPE_KEY[params.entityType]
        ? t(ENTITY_TYPE_KEY[params.entityType])
        : params.entityType;
    return t(key, { ...params, entityType });
};